// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { monthMap, monthMinMax, yearsFromNow } from "@werner/App/Components/XngExpirationDatePicker/utils/constants";
import {
    CalendarUnit,
    CalendarView,
    HasAvailableUnits,
    YearFormat,
} from "@werner/App/Components/XngExpirationDatePicker/utils/types";

export const convertStringToNumber = (value: string): number => {
    return +value;
};

export const validateMonth = (valueAsString: string, oldValue: string): string => {
    const [min, max] = monthMinMax;
    const value = convertStringToNumber(valueAsString);
    const validator = value >= min && value <= max && /^\d*$/.test(valueAsString);
    if (validator) {
        return valueAsString;
    }

    return oldValue;
};

export const validateYear = (
    valueAsString: string,
    oldValue: string,
    format: YearFormat,
    lastAvailableYear?: number,
): string => {
    const { fullYear: fullYearPart, lastTwo: lastTwoPart } = getYearParts();
    const fullYear = convertStringToNumber(fullYearPart);
    const lastTwo = convertStringToNumber(lastTwoPart);
    const value = convertStringToNumber(valueAsString);

    const year2DigitsValidator =
        format === "2-digits" && ((valueAsString.length === 2 && value >= lastTwo) || valueAsString.length < 2);

    const year4DigitsValidator =
        format === "4-digits" &&
        ((valueAsString.length === 4 && value >= fullYear && value <= (lastAvailableYear || fullYear + yearsFromNow)) ||
            valueAsString.length < 4);

    const validator = year2DigitsValidator || year4DigitsValidator;
    if (validator) {
        return valueAsString;
    }

    return oldValue;
};

export const yearOnSubmitValidator = (yearProp: string | number, lastAvailableYear?: number): boolean => {
    const year = convertStringToNumber(`${yearProp}`);
    const fullYear = convertStringToNumber(getYearParts().fullYear);

    if (!yearProp || Number.isNaN(year)) {
        return false;
    }

    return `${yearProp}`.length === 4 && year >= fullYear && year <= (lastAvailableYear || fullYear + yearsFromNow);
};

export const monthOnSubmitValidator = (monthProp: string | number) => {
    const [min, max] = monthMinMax;
    const month = convertStringToNumber(`${monthProp}`);

    if (!monthProp || Number.isNaN(month)) {
        return false;
    }
    return month >= min && month <= max;
};

export const getValueOrUndefined = (numProp?: string | number, cb?: (value: number) => string | number): any => {
    if (numProp === undefined) {
        return undefined;
    }

    const value = convertStringToNumber(`${numProp}`);
    if (cb) {
        return cb(value);
    }
    return value;
};

export const createYearsMap = (): CalendarUnit[] => {
    const currentYear = new Date().getFullYear();
    const yearsMap = new Array(10)
        .fill(null)
        .map((_, index) => ({ key: `${currentYear + index}`, value: currentYear + index }));
    return yearsMap;
};

export const getYearValue = (year: number, format: YearFormat) => {
    const { lastTwo, fullYear } = getYearParts(year);
    return format === "2-digits" ? lastTwo : fullYear;
};

export const getYearParts = (year?: number) => {
    const currentYear = new Date().getFullYear();
    const yearValue = year ?? currentYear;
    const is4digits = year?.toString().length === 4;
    const century = `${currentYear}`.slice(0, currentYear.toString().length - 2);
    const fullYear = `${century}${yearValue.toString().slice(-2)}`;
    const lastTwo = !is4digits && year !== undefined ? `${year}` : `${fullYear}`.substr(-2);

    return {
        fullYear,
        lastTwo,
        century,
    };
};

export const formatValue = (valueProp?: string | number) => {
    if (!valueProp) {
        return "";
    }

    const num = convertStringToNumber(`${valueProp}`);
    if (Number.isNaN(num)) {
        return "";
    }

    return num < 10 ? `${num}`.padStart(2, "0") : `${num}`;
};

/**
 * multiply by 8 for each character else if value = undefined return 25
 * */
export const getInputWidth = (value?: string) => {
    return (value?.length || 0) * 8 || 25;
};

export const getCalendarUnitMap = (calendarView: CalendarView, sourceMap: HasAvailableUnits) => {
    const { availableMonths, availableYears } = sourceMap;

    if (calendarView === "month") {
        return availableMonths && availableMonths.length > 0 ? availableMonths : monthMap;
    }

    return availableYears && availableYears.length > 0 ? availableYears : createYearsMap();
};

export const getCalendarLabel = (calendarView: CalendarView, years: CalendarUnit[], selectedYear?: number) => {
    if (calendarView === "month") {
        return `${selectedYear}`;
    }

    if (years.length === 1) {
        return `${arrLast(years)?.key}`;
    }

    return `${arrFirst(years)?.key} - ${arrLast(years)?.key}`;
};

export const arrLast = <T>(array?: T[]) => {
    return array && array[array.length - 1];
};

export const arrFirst = <T>(array?: T[]) => {
    return array && array[0];
};
